import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Megaphone, Star, BarChart3, TrendingUp, Tv, Smartphone, Zap, Globe } from 'lucide-react';
import SatisfactionDashboard from './SatisfactionDashboard';
import AdAnalyticsDashboard from './AdAnalyticsDashboard';
import AdManager from './AdManager';
import { getGroupPreferences } from '../services/cloudflareService';

type SubTab = 'ads' | 'satisfaction' | 'ads_global' | 'ads_local';

interface MarketingDashboardProps {
    theme: 'dark' | 'light';
    hospitals: any[];
    // Ad Analytics props
    hospitalId?: string;
    groupId?: string;
    group?: any;
    adAssets?: any[];
    // Initial sub-tab (optional)
    initialTab?: SubTab;
}

const MarketingDashboard: React.FC<MarketingDashboardProps> = ({
    theme, hospitals, hospitalId, groupId, group, adAssets = [], initialTab
}) => {
    const [activeSubTab, setActiveSubTab] = useState<SubTab>(initialTab || (groupId ? 'ads_global' : 'ads_local'));
    const [globalAssets, setGlobalAssets] = useState<any[]>([]);
    const [globalSettings, setGlobalSettings] = useState<any>(null);
    const [loadingGlobal, setLoadingGlobal] = useState(false);

    React.useEffect(() => {
        if (groupId && activeSubTab === 'ads_global') {
            const loadGlobal = async () => {
                setLoadingGlobal(true);
                try {
                    const prefs = await getGroupPreferences(groupId);
                    if (prefs?.globalAdAssets) setGlobalAssets(prefs.globalAdAssets);
                    if (prefs?.globalAdSettings) setGlobalSettings(prefs.globalAdSettings);
                } catch (e) {
                    console.error("Error loading global assets", e);
                }
                setLoadingGlobal(false);
            };
            loadGlobal();
        }
    }, [groupId, activeSubTab]);

    const t = useMemo(() => ({
        textWhite: theme === 'dark' ? 'text-white' : 'text-slate-900',
        textMuted: theme === 'dark' ? 'text-slate-400' : 'text-slate-500',
        cardBg: theme === 'dark' ? 'bg-white/[0.02] backdrop-blur-2xl' : 'bg-white/70 backdrop-blur-2xl',
        cardBorder: theme === 'dark' ? 'border-white/[0.05]' : 'border-slate-200/60',
        segmentBg: theme === 'dark' ? 'bg-white/[0.04]' : 'bg-slate-100/80',
        segmentActiveBg: theme === 'dark'
            ? 'bg-gradient-to-r from-indigo-600 to-violet-600 shadow-[0_0_20px_rgba(99,102,241,0.35)]'
            : 'bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)]',
        segmentActiveText: theme === 'dark' ? 'text-white' : 'text-slate-900',
        segmentInactiveText: theme === 'dark' ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-700',
    }), [theme]);

    const subTabs = [
        ...(groupId ? [{
            id: 'ads_global' as SubTab,
            label: 'Campaña Global',
            icon: Globe,
            description: 'Gestionar publicidad para toda la cadena',
        }] : [
            {
                id: 'ads_local' as SubTab,
                label: 'Tus Anuncios',
                icon: Megaphone,
                description: 'Gestionar publicidad local',
            }
        ]),
        {
            id: 'ads' as SubTab,
            label: 'Métricas',
            icon: TrendingUp,
            description: 'Impresiones, clicks y rendimiento',
        },
        {
            id: 'satisfaction' as SubTab,
            label: 'Satisfacción',
            icon: Star,
            description: 'Opiniones y Google Reviews',
        },
    ];

    return (
        <div className="space-y-6">
            {/* ── Header with Segmented Control ── */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2.5 rounded-xl ${theme === 'dark' ? 'bg-gradient-to-br from-violet-500/20 to-indigo-500/20 border border-violet-500/20' : 'bg-gradient-to-br from-violet-50 to-indigo-50 border border-violet-200'} shadow-[0_0_15px_rgba(139,92,246,0.15)]`}>
                        <TrendingUp className="w-6 h-6 text-violet-500" />
                    </div>
                    <div>
                        <h2 className={`text-xl font-black ${t.textWhite} leading-tight tracking-tight`}>
                            Marketing & Reputación
                        </h2>
                        <p className={`text-xs ${t.textMuted} font-medium`}>
                            Publicidad, satisfacción y funnel de Google Reviews
                        </p>
                    </div>
                </div>

                {/* Segmented Control */}
                <div className={`${t.segmentBg} p-1 rounded-2xl border ${t.cardBorder} flex gap-1 self-start sm:self-center overflow-x-auto`}>
                    {subTabs.map((tab) => {
                        const isActive = activeSubTab === tab.id;
                        return (
                            <button
                                key={tab.id}
                                onClick={() => setActiveSubTab(tab.id)}
                                className={`relative flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all duration-300 ${
                                    isActive
                                        ? `${t.segmentActiveBg} ${t.segmentActiveText}`
                                        : `${t.segmentInactiveText}`
                                }`}
                            >
                                <tab.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? '' : 'opacity-70'}`} />
                                <span className="whitespace-nowrap">{tab.label}</span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ── Sub-tab Content ── */}
            <AnimatePresence mode="wait">
                {activeSubTab === 'ads_global' && groupId && (
                    <motion.div
                        key="ads_global"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                    >
                        {loadingGlobal ? (
                            <div className="py-20 flex justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                        ) : (
                            <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-2 sm:p-4">
                                <AdManager
                                    hospital={hospitals[0] || { id: 'DUMMY', branding: { primaryColor: '#4F46E5' } }}
                                    onUpdate={() => {}}
                                    globalMode={true}
                                    groupId={groupId}
                                    globalAssets={globalAssets}
                                    onUpdateGlobalAssets={setGlobalAssets}
                                    globalSettings={globalSettings}
                                    onUpdateGlobalSettings={setGlobalSettings}
                                />
                            </div>
                        )}
                    </motion.div>
                )}

                {activeSubTab === 'ads_local' && !groupId && (
                    <motion.div
                        key="ads_local"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                    >
                        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-2 sm:p-4">
                            <AdManager
                                hospital={hospitals[0]}
                                onUpdate={() => {}}
                                globalAssets={globalAssets} // Could be populated if we want
                            />
                        </div>
                    </motion.div>
                )}

                {activeSubTab === 'ads' && (
                    <motion.div
                        key="ads"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                    >
                        {/* Inventory KPIs (migrated from Analytics) */}
                        {(() => {
                            const activeAds = adAssets.filter(a => a.isActive);
                            const tvCount = activeAds.filter(a => a.target === 'ALL' || a.target === 'TV_ONLY').length;
                            const mobileCount = activeAds.filter(a => a.target === 'ALL' || a.target === 'MOBILE_ONLY').length;
                            return (
                                <div className={`grid grid-cols-3 gap-4 mb-6`}>
                                    <div className={`${t.cardBg} border ${t.cardBorder} rounded-2xl p-5 flex items-center gap-4`}>
                                        <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-amber-50 text-amber-600 border border-amber-200'}`}>
                                            <Zap className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Anuncios Activos</p>
                                            <p className={`text-2xl font-black ${t.textWhite} font-mono`}>{activeAds.length}</p>
                                        </div>
                                    </div>
                                    <div className={`${t.cardBg} border ${t.cardBorder} rounded-2xl p-5 flex items-center gap-4`}>
                                        <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-indigo-500/10 text-indigo-400 border border-indigo-500/30' : 'bg-indigo-50 text-indigo-600 border border-indigo-200'}`}>
                                            <Tv className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Exposición TV</p>
                                            <p className={`text-2xl font-black ${t.textWhite} font-mono`}>{tvCount}</p>
                                        </div>
                                    </div>
                                    <div className={`${t.cardBg} border ${t.cardBorder} rounded-2xl p-5 flex items-center gap-4`}>
                                        <div className={`p-3 rounded-xl ${theme === 'dark' ? 'bg-pink-500/10 text-pink-400 border border-pink-500/30' : 'bg-pink-50 text-pink-600 border border-pink-200'}`}>
                                            <Smartphone className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <p className={`text-[10px] font-bold ${t.textMuted} uppercase tracking-widest`}>Banners Móvil</p>
                                            <p className={`text-2xl font-black ${t.textWhite} font-mono`}>{mobileCount}</p>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}
                        <AdAnalyticsDashboard
                            theme={theme}
                            hospitalId={hospitalId}
                            groupId={groupId}
                            hospitals={hospitals}
                            adAssets={adAssets}
                        />
                    </motion.div>
                )}

                {activeSubTab === 'satisfaction' && (
                    <motion.div
                        key="satisfaction"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                    >
                        <SatisfactionDashboard
                            theme={theme}
                            hospitals={hospitals}
                        />
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default MarketingDashboard;
